import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, IconButton, Typography, Box,
  MenuItem, CircularProgress, Tooltip, Checkbox, FormControlLabel
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import { postData, updateData, fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { printBillPDF, shareBillWhatsApp } from '../../../utils/billPdfGenerator';
import LogoLoader from '../../common/LogoLoader';

const PAYMENT_OPTIONS = [
  { value: 1, label: 'Cash' },
  { value: 2, label: 'Card' },
  { value: 3, label: 'UPI' },
];

const today = () => new Date().toISOString().split('T')[0];

const EMPTY_HEADER = {
  BillNumber: '', BillDate: today(),
  ClientId: null, ClientName: '', ContactNumber: '',
  Remarks: '', FinYear: '',
  Amount: 0, OtherAmount: 0, PackingCharge: 0, TotalAmount: 0,
  AdvanceAmount: 0, PaidAmount: 0, CardAmount: 0, UpiAmount: 0,
  BalanceAmount: 0, Roundoff: 0, DiscountRate: 0,
  PaymentType: 1, PaymentBankId: null, OrderId: null,
  IsPettyCash: false,
};

const EMPTY_DETAIL = {
  Width: '', Height: '', MountWidth: '',
  SizeId: null, FrameId: null, Thicknes: '', Length: '',
  GlassId: null, MountId: null, BoardId: null,
  LaminationId: null, TapeId: null, HookId: null, HookCount: 0,
  StandId: null, PrintId: null,
  Quantity: 1, DiscAmt: 0, Discper: 0, Amount: 0, Rate: 0,
  Perimeter: 0, Area: 0,
  FrameRate: 0, GlassRate: 0, MountRate: 0, BoardRate: 0,
  LaminationRate: 0, TapeRate: 0, StandRate: 0, StandLabourCharge: 0,
  PrintRate: 0,
};

function calcRowAmount(row, sizesList, printsList, hooksList, companySettings) {
  const {
    TapeWastage = 0, MountWastage = 0, FrameWastage = 0,
    GLassWastage = 0, BoardWastage = 0, LaminationWastage = 0,
    TotalMargin: TotalMarginper = 0,
  } = companySettings || {};

  const w       = parseFloat(row.Width)      || 0;
  const h       = parseFloat(row.Height)     || 0;
  const mw      = parseFloat(row.MountWidth) || 0;
  const thick   = parseFloat(row.Thicknes)   || 0;
  const qty     = parseFloat(row.Quantity)   || 1;
  const discAmt = parseFloat(row.DiscAmt)    || 0;
  const discPer = parseFloat(row.Discper)    || 0;

  const wCalc     = Math.round(w     * 2 * 100) / 100;
  const hCalc     = Math.round(h     * 2 * 100) / 100;
  const thCalc    = Math.round(thick * 8 * 100) / 100;
  const mwCalc    = Math.round(mw    * 8 * 100) / 100;
  const perimeter = Math.round((wCalc + hCalc + mwCalc + thCalc) * 100) / 100;
  const area      = (w + mw * 2) * (h + mw * 2);

  let printarea = w * h;
  if (row.PrintId) {
    const printer = printsList.find(p => p.Id === row.PrintId);
    if (printer && printer.RollWidth) printarea = printer.RollWidth * h;
  }

  const GlassRate      = Math.round((row.GlassRate      || 0) * area      * 100) / 100;
  const PrintRate      = Math.round((row.PrintRate      || 0) * printarea  * 100) / 100;
  const LaminationRate = Math.round((row.LaminationRate || 0) * area      * 100) / 100;
  const BoardRate      = Math.round((row.BoardRate      || 0) * area      * 100) / 100;
  const MountRate      = Math.round((row.MountRate      || 0) * area      * 100) / 100;
  const TapeRate       = Math.round((row.TapeRate       || 0) * perimeter * 100) / 100;
  const FrameRate      = Math.round((row.FrameRate      || 0) * perimeter * 100) / 100;
  const StandRate      = parseFloat(row.StandRate)         || 0;
  const StandLabour    = parseFloat(row.StandLabourCharge) || 0;

  const framemargin      = FrameWastage      ? Math.round(FrameRate      * FrameWastage      / 100 * 100) / 100 : 0;
  const mountmargin      = MountWastage      ? Math.round(MountRate      * MountWastage      / 100 * 100) / 100 : 0;
  const tapemargin       = TapeWastage       ? Math.round(TapeRate       * TapeWastage       / 100 * 100) / 100 : 0;
  const glassmargin      = GLassWastage      ? Math.round(GlassRate      * GLassWastage      / 100 * 100) / 100 : 0;
  const boardmargin      = BoardWastage      ? Math.round(BoardRate      * BoardWastage      / 100 * 100) / 100 : 0;
  const laminationmargin = LaminationWastage ? Math.round(LaminationRate * LaminationWastage / 100 * 100) / 100 : 0;

  const sizeDetail = sizesList.find(s => s.AreaFrom <= area && s.AreaTo >= area) || null;

  const LabourBoard      = (row.BoardRate      || 0) === 0 ? 0 : (sizeDetail ? sizeDetail.LabourBoard      || 0 : 0);
  const LabourFrame      = (row.FrameRate      || 0) === 0 ? 0 : (sizeDetail ? sizeDetail.LabourFrame      || 0 : 0);
  const LabourGlass      = (row.GlassRate      || 0) === 0 ? 0 : (sizeDetail ? sizeDetail.LabourGlass      || 0 : 0);
  const LabourMount      = (row.MountRate      || 0) === 0 ? 0 : (sizeDetail ? sizeDetail.LabourMount      || 0 : 0);
  const LabourLamination = (row.LaminationRate || 0) === 0 ? 0 : (sizeDetail ? sizeDetail.LabourLamination || 0 : 0);

  let HookRate = 0;
  const hookCount = parseInt(row.HookCount) || 0;
  if (hookCount > 0 && sizeDetail && sizeDetail.HookRate) {
    HookRate = Math.round(hookCount * sizeDetail.HookRate * 100) / 100;
  }
  const hookDetail = hooksList.find(h => h.Name === (sizeDetail ? sizeDetail.Name : '')) || null;
  const resolvedHookId = hookDetail ? hookDetail.Id : null;

  let totamt =
    framemargin + mountmargin + glassmargin + boardmargin + laminationmargin +
    LabourBoard + tapemargin + LabourFrame + LabourGlass + LabourMount + LabourLamination +
    FrameRate + StandRate + StandLabour + HookRate +
    TapeRate + LaminationRate + BoardRate + MountRate + GlassRate;

  const totalmargin = TotalMarginper ? Math.round(totamt * TotalMarginper / 100 * 100) / 100 : 0;
  totamt = Math.round((totamt + PrintRate + totalmargin) * 100) / 100;


  if (discPer !== 0) {
    const discountAmt = Math.round(totamt * discPer / 100 * 100) / 100;
    totamt = Math.round((totamt - discountAmt) * 100) / 100;
  }

  const rate        = totamt;
  const finalAmount = Math.round((totamt - discAmt) * qty * 100) / 100;

  return {
    perimeter,
    area:   Math.round(area * 10000) / 10000,
    Rate:   rate,
    Amount: finalAmount,
    SizeId: sizeDetail ? sizeDetail.Id : null,
    HookId: resolvedHookId,
    Length: perimeter,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
const BillModal = ({ open, onClose, onSave, BillData = null, mode = 'create', companyData = {}, initialData = null }) => {
  const { companyId, userId, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [formData, setFormData] = useState(EMPTY_HEADER);
  const [errors, setErrors] = useState({});
  const [details, setDetails] = useState([]);
  const [editingRow, setEditingRow] = useState(null);

  const [masters, setMasters] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [frames, setFrames] = useState([]);
  const [glasses, setGlasses] = useState([]);
  const [mounts, setMounts] = useState([]);
  const [boards, setBoards] = useState([]);
  const [laminations, setLaminations] = useState([]);
  const [tapes, setTapes] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [stands, setStands] = useState([]);
  const [prints, setPrints] = useState([]);
  const [lookupsLoaded, setLookupsLoaded] = useState(false);
  const [companySettings, setCompanySettings] = useState({});

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!open || lookupsLoaded || !companyId || !token) return;
    const load = async (ctrl, setter) => {
      try {
        const r = await fetchData(ctrl, `/${companyId}`, token);
        if (r && r.responseCode === '1111' && r.response_data)
          setter(Array.isArray(r.response_data) ? r.response_data : [r.response_data]);
      } catch (_) {}
    };
    Promise.all([
      load('MasterApi', setMasters),
      load('SizeApi', setSizes),
      load('FrameApi', setFrames),
      load('GlassApi', setGlasses),
      load('MountApi', setMounts),
      load('BoardApi', setBoards),
      load('LaminationApi', setLaminations),
      load('TapeApi', setTapes),
      load('HookApi', setHooks),
      load('StandApi', setStands),
      load('PrintApi', setPrints),
      fetchData('CompanyApi', `/Details/${companyId}`, token)
        .then(r => { if (r && r.responseCode === '1111' && r.response_data) setCompanySettings(r.response_data); })
        .catch(() => {}),
    ]).then(() => setLookupsLoaded(true));
  }, [open, lookupsLoaded, companyId, token]);

  useEffect(() => {
    if (!open) return;
    if (BillData && mode !== 'create') {      setLoading(true);
      fetchData('BillApi', `/${BillData.Id}`, token)
        .then(r => {
          const d = (r && r.responseCode === '1111' && r.response_data) ? r.response_data : null;
          if (d) {
            setFormData({
              BillNumber:    d.Bill.BillNumber    ?? '',
              BillDate:      d.Bill.BillDate      ? d.Bill.BillDate.split('T')[0]  : today(),
              ClientId:      d.Bill.ClientId      ?? null,
              ClientName:    d.Bill.ClientName    ?? '',
              ContactNumber: d.Bill.ContactNumber ?? '',
              Remarks:       d.Bill.Remarks       ?? '',
              FinYear:       d.Bill.FinYear       ?? '',
              Amount:        d.Bill.Amount        ?? 0,
              OtherAmount:   d.Bill.OtherAmount   ?? 0,
              PackingCharge: d.Bill.PackingCharge ?? 0,
              TotalAmount:   d.Bill.TotalAmount   ?? 0,
              AdvanceAmount: d.Bill.AdvanceAmount ?? 0,
              PaidAmount:    d.Bill.PaidAmount    ?? 0,
              CardAmount:    d.Bill.CardAmount    ?? 0,
              UpiAmount:     d.Bill.UpiAmount     ?? 0,
              BalanceAmount: d.Bill.BalanceAmount ?? 0,
              Roundoff:      d.Bill.Roundoff      ?? 0,
              DiscountRate:  d.Bill.DiscountRate  ?? 0,
              PaymentType:   parseInt(d.Bill.PaymentType) || 1,
              PaymentBankId: d.Bill.PaymentBankId ?? null,
              IsPettyCash:   d.Bill.IsPettyCash ?? false,
            });
            setDetails(d.BillDetails ?? []);
          } else {
            setFormData({
              BillNumber:    BillData.BillNumber    ?? '',
              BillDate:      BillData.BillDate      ? BillData.BillDate.split('T')[0] : today(),
              ClientId:      BillData.ClientId      ?? null,
              ClientName:    BillData.ClientName    ?? '',
              ContactNumber: BillData.ContactNumber ?? '',
              Remarks:       BillData.Remarks       ?? '',
              FinYear:       BillData.FinYear       ?? '',
              Amount:        BillData.Amount        ?? 0,
              OtherAmount:   BillData.OtherAmount   ?? 0,
              PackingCharge: BillData.PackingCharge ?? 0,
              TotalAmount:   BillData.TotalAmount   ?? 0,
              AdvanceAmount: BillData.AdvanceAmount ?? 0,
              PaidAmount:    BillData.PaidAmount    ?? 0,
              CardAmount:    BillData.CardAmount    ?? 0,
              UpiAmount:     BillData.UpiAmount     ?? 0,
              BalanceAmount: BillData.BalanceAmount ?? 0,
              Roundoff:      BillData.Roundoff      ?? 0,
              DiscountRate:  BillData.DiscountRate  ?? 0,
              PaymentType:   parseInt(BillData.PaymentType) || 1,
              PaymentBankId: BillData.PaymentBankId ?? null,
              IsPettyCash:   BillData.IsPettyCash ?? false,
            });
            setDetails(BillData.BillDetails ?? []);
          }
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } else if (initialData) {
      // Pre-fill from estimate (Convert to Bill)
      setFormData({ ...EMPTY_HEADER, ...initialData, BillNumber: '' });
      setDetails(initialData.BillDetails ?? []);
      if (companyId && token) {
        fetchData('BillApi', `/MaxBillno/${companyId}`, token)
          .then(r => {
            const data = r && (r.response_data ?? r.responseData ?? r);
            const next = (parseInt(data?.OrderNumber ?? data) || 0) + 1;
            const finYear = data?.FinYear ?? '';
            setFormData(prev => ({ ...prev, BillNumber: next, ...(finYear && { FinYear: finYear }) }));
          })
          .catch(() => {});
      }
    } else {
      setFormData(EMPTY_HEADER);
      setDetails([]);
      if (companyId && token) {
        fetchData('BillApi', `/MaxBillno/${companyId}`, token)
          .then(r => {
            const data = r && (r.response_data ?? r.responseData ?? r);
            const next = (parseInt(data?.OrderNumber ?? data) || 0) + 1;
            const finYear = data?.FinYear ?? '';
            setFormData(prev => ({ ...prev, BillNumber: next, ...(finYear && { FinYear: finYear }) }));
          })
          .catch(() => {});
      }
    }
    setErrors({});
    setEditingRow(null);
  }, [open, BillData, mode, initialData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculate totals
  useEffect(() => {
    const subTotal = details.reduce((s, d) => s + (parseFloat(d.Amount) || 0), 0);
    const disc     = parseFloat(formData.DiscountRate) || 0;
    const discAmt  = (subTotal * disc) / 100;
    const packing  = parseFloat(formData.PackingCharge) || 0;
    const other    = parseFloat(formData.OtherAmount) || 0;
    const raw      = subTotal - discAmt + packing + other;
    const roundoff = Math.round(raw) - raw;
    const total    = raw + roundoff;
    const advance  = parseFloat(formData.AdvanceAmount) || 0;
    const paid     = parseFloat(formData.PaidAmount) || 0;
    const card     = parseFloat(formData.CardAmount) || 0;
    const upi      = parseFloat(formData.UpiAmount) || 0;
    const balance  = total - advance - paid - card - upi;
    setFormData(prev => ({
      ...prev,
      Amount:        parseFloat(subTotal.toFixed(2)),
      Roundoff:      parseFloat(roundoff.toFixed(2)),
      TotalAmount:   parseFloat(total.toFixed(2)),
      BalanceAmount: parseFloat(balance.toFixed(2)),
    }));
  }, [details, formData.DiscountRate, formData.OtherAmount, formData.PackingCharge,
      formData.AdvanceAmount, formData.PaidAmount, formData.CardAmount, formData.UpiAmount]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const paymentFields = ['AdvanceAmount', 'PaidAmount', 'CardAmount', 'UpiAmount'];
    if (paymentFields.includes(name)) {
      const newVal = parseFloat(value) || 0;
      if (newVal < 0) return;
      setFormData(prev => {
        const updated  = { ...prev, [name]: value };
        const advance  = parseFloat(name === 'AdvanceAmount' ? value : prev.AdvanceAmount) || 0;
        const paid     = parseFloat(name === 'PaidAmount'    ? value : prev.PaidAmount)    || 0;
        const card     = parseFloat(name === 'CardAmount'    ? value : prev.CardAmount)    || 0;
        const upi      = parseFloat(name === 'UpiAmount'     ? value : prev.UpiAmount)     || 0;
        const total    = parseFloat(prev.TotalAmount) || 0;
        const balance  = parseFloat((total - advance - paid - card - upi).toFixed(2));
        if (balance < 0) return prev;
        return { ...updated, BalanceAmount: balance };
      });
      if (errors.payment) setErrors(prev => ({ ...prev, payment: '' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleClientSelect = (_, selected) => {
    if (selected) {
      setFormData(prev => ({
        ...prev,
        ClientId:      selected.Id,
        ClientName:    selected.Name || '',
        ContactNumber: selected.ContactNumber || '',
      }));
    } else {
      setFormData(prev => ({ ...prev, ClientId: null, ClientName: '', ContactNumber: '' }));
    }
  };

  const addRow = () => {
    const newRow = { ...EMPTY_DETAIL, _key: Date.now() };
    setDetails(prev => [...prev, newRow]);
    setEditingRow(details.length);
  };

  const updateDetailField = (idx, field, value) => {
    setDetails(prev => {
      const updated = [...prev];
      let row = { ...updated[idx], [field]: value };

      const rateMap = {
        FrameId:      { list: frames,      rateField: 'FrameRate',
                        extra: (item, r) => { r.Thicknes = item ? item.Thickness || 0 : 0; } },
        GlassId:      { list: glasses,     rateField: 'GlassRate' },
        MountId:      { list: mounts,      rateField: 'MountRate' },
        BoardId:      { list: boards,      rateField: 'BoardRate' },
        LaminationId: { list: laminations, rateField: 'LaminationRate' },
        TapeId:       { list: tapes,       rateField: 'TapeRate' },
        StandId:      { list: stands,      rateField: 'StandRate',
                        extraField: 'StandLabourCharge', extraKey: 'LabourCharge' },
        PrintId:      { list: prints,      rateField: 'PrintRate' },
      };
      if (rateMap[field]) {
        const { list, rateField, extraField, extraKey, extra } = rateMap[field];
        const item = list.find(x => x.Id === parseInt(value));
        row[rateField] = item ? item.Rate || 0 : 0;
        if (extraField) row[extraField] = item ? item[extraKey] || 0 : 0;
        if (extra) extra(item, row);
      }

      const { perimeter, area, Rate, Amount, SizeId, HookId, Length } =
        calcRowAmount(row, sizes, prints, hooks, companySettings);
      row.Perimeter = perimeter;
      row.Area      = area;
      row.Rate      = Rate;
      row.Amount    = Amount;
      row.SizeId    = SizeId;
      row.HookId    = HookId;
      row.Length    = Length;

      updated[idx] = row;
      return updated;
    });
  };

  const validate = () => {
    const e = {};
    if (!formData.ClientName && !formData.ClientId) e.ClientName = 'Client is required';
    const total   = parseFloat(formData.TotalAmount) || 0;
    const advance = parseFloat(formData.AdvanceAmount) || 0;
    const paid    = parseFloat(formData.PaidAmount) || 0;
    const card    = parseFloat(formData.CardAmount) || 0;
    const upi     = parseFloat(formData.UpiAmount) || 0;
    const balance = parseFloat(formData.BalanceAmount) || 0;
    const paySum  = parseFloat((advance + paid + card + upi + balance).toFixed(2));
    if (balance < 0) {
      e.payment = `Payments exceed Total. Paid: ${(advance + paid + card + upi).toFixed(2)}, Total: ${total.toFixed(2)}`;
    } else if (paySum !== total) {
      e.payment = `Payment mismatch: Advance+Cash+Card+UPI+Balance (${paySum.toFixed(2)}) must equal Total (${total.toFixed(2)})`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const dataToSend = {
        Bill: {
          Id: mode === 'edit' ? (BillData?.Id || 0) : 0,
          CompanyId: companyId,
          BillNumber: parseInt(formData.BillNumber) || 0,
          BillDate: formData.BillDate || today(),
          ClientId: formData.ClientId || null,
          ClientName: formData.ClientName || '',
          ContactNumber: formData.ContactNumber || '',
          Remarks: formData.Remarks || '',
          FinYear: formData.FinYear || '',
          PaymentType: parseInt(formData.PaymentType) || 1,
          PaymentBankId: formData.PaymentBankId || null,
          OrderId: formData.OrderId || null,
          DiscountRate: parseFloat(formData.DiscountRate) || 0,
          Amount: parseFloat(formData.Amount) || 0,
          OtherAmount: parseFloat(formData.OtherAmount) || 0,
          PackingCharge: parseFloat(formData.PackingCharge) || 0,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          AdvanceAmount: parseFloat(formData.AdvanceAmount) || 0,
          PaidAmount: parseFloat(formData.PaidAmount) || 0,
          CardAmount: parseFloat(formData.CardAmount) || 0,
          UpiAmount: parseFloat(formData.UpiAmount) || 0,
          BalanceAmount: parseFloat(formData.BalanceAmount) || 0,
          Roundoff: Math.round((parseFloat(formData.Roundoff) || 0) * 100) / 100,
          IsPettyCash: formData.IsPettyCash || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: mode === 'create' ? userId : (BillData?.CreatedBy || userId),
          CreatedDate: mode === 'create' ? null : (BillData?.CreatedDate || null),
          ModifiedBy: mode === 'edit' ? userId : null,
          ModifiedDate: mode === 'edit' ? new Date().toISOString() : null,
        },
        BillDetails: details.map(d => ({
          Id: mode === 'edit' ? (d.Id || 0) : 0,
          CompanyId: companyId,
          BillId: mode === 'edit' ? (BillData?.Id || 0) : 0,
          SizeId: d.SizeId || null,
          FrameId: d.FrameId || null,
          GlassId: d.GlassId || null,
          MountId: d.MountId || null,
          BoardId: d.BoardId || null,
          LaminationId: d.LaminationId || null,
          TapeId: d.TapeId || null,
          HookId: d.HookId || null,
          HookCount: parseInt(d.HookCount) || 0,
          StandId: d.StandId || null,
          PrintId: d.PrintId || null,
          Width: parseFloat(d.Width) || 0,
          Height: parseFloat(d.Height) || 0,
          MountWidth: parseFloat(d.MountWidth) || 0,
          Thicknes: parseFloat(d.Thicknes) || 0,
          Length: parseFloat(d.Length) || 0,
          Perimeter: parseFloat(d.Perimeter) || 0,
          Area: parseFloat(d.Area) || 0,
          Quantity: parseInt(d.Quantity) || 1,
          Rate: parseFloat(d.Rate) || 0,
          Amount: parseFloat(d.Amount) || 0,
          DiscAmt: parseFloat(d.DiscAmt) || 0,
          Discper: parseFloat(d.Discper) || 0,
          FrameRate: parseFloat(d.FrameRate) || 0,
          GlassRate: parseFloat(d.GlassRate) || 0,
          MountRate: parseFloat(d.MountRate) || 0,
          BoardRate: parseFloat(d.BoardRate) || 0,
          LaminationRate: parseFloat(d.LaminationRate) || 0,
          TapeRate: parseFloat(d.TapeRate) || 0,
          StandRate: parseFloat(d.StandRate) || 0,
          StandLabourCharge: parseFloat(d.StandLabourCharge) || 0,
          PrintRate: parseFloat(d.PrintRate) || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: mode === 'create' ? userId : (d.CreatedBy || userId),
          CreatedDate: mode === 'create' ? null : (d.CreatedDate || null),
          ModifiedBy: mode === 'edit' ? userId : null,
          ModifiedDate: mode === 'edit' ? new Date().toISOString() : null,
        })),
      };
      if (mode === 'edit') {
        await updateData('BillApi', BillData.Id, dataToSend, token);
      } else {
        await postData('BillApi', '', dataToSend, token);
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving Bill:', error);
    } finally {
      setLoading(false);
    }
  };

  const isReadOnly = mode === 'view';
  const getTitle  = () => ({ create: 'New Bill', edit: 'Edit Bill', view: 'Bill Details' }[mode] || 'Bill');

  const enrichDetailsForPDF = (rawDetails) => rawDetails.map(d => ({
    ...d,
    Frame:      { Name: frames.find(x => x.Id === d.FrameId)?.Name || '' },
    Glass:      { Name: glasses.find(x => x.Id === d.GlassId)?.Name || '' },
    Lamination: { Name: laminations.find(x => x.Id === d.LaminationId)?.Name || '' },
    Mount:      { Name: mounts.find(x => x.Id === d.MountId)?.Name || '' },
    Board:      { Name: boards.find(x => x.Id === d.BoardId)?.Name || '' },
    Stand:      { Name: stands.find(x => x.Id === d.StandId)?.Name || '' },
    Tape:       { Name: tapes.find(x => x.Id === d.TapeId)?.Name || '' },
    Print:      { PrintType: prints.find(x => x.Id === d.PrintId)?.PrintType || prints.find(x => x.Id === d.PrintId)?.Name || '' },
  }));

  const handlePrintPDF = () => {
    const pdfData = { ...formData, BillDetails: enrichDetailsForPDF(details) };
    printBillPDF(pdfData, companyData);
  };

  const handleWhatsApp = () => {
    const pdfData = { ...formData, BillDetails: enrichDetailsForPDF(details) };
    shareBillWhatsApp(pdfData, companyData);
  };

  const inp      = { borderRadius: '4px', fontSize: '12px', height: '30px' };
  const lbl      = { fontSize: '11px' };
  const hlp      = { fontSize: '9px', margin: '1px 0 0 0' };
  const readBg   = { backgroundColor: '#f5f5f5' };
  const footerInp = { borderRadius: '4px', fontSize: '12px', textAlign: 'right' };
  const footerAmt = { ...footerInp, backgroundColor: '#f0f7f0', fontWeight: '600' };
  const footerLbl = { fontSize: '11px' };

  const selectedClient = masters.find(m => m.Id === formData.ClientId) || null;
  const lookupName = (arr, id) => arr.find(x => x.Id === id)?.Name || '—';

  return (
    <Dialog
      open={open} onClose={onClose}
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{
        style: {
          width: isMobile ? '100%' : '99vw',
          maxWidth: '99vw',
          borderRadius: isMobile ? '0' : '12px',
          background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)',
          padding: '2px',
          margin: isMobile ? '0' : '4px',
        }
      }}
    >
      <Box style={{ background: 'white', borderRadius: isMobile ? '0' : '10px', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: isMobile ? '100vh' : 'auto', maxHeight: isMobile ? '100vh' : '95vh' }}>
        {loading && <LogoLoader open={loading} message="Loading Bill..." />}

        {/* ── Title Bar ── */}
        <DialogTitle style={{ background: 'linear-gradient(135deg, #3b2410 0%, #5c3d1e 100%)', color: 'white', padding: '8px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 'auto', flexShrink: 0 }}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <ReceiptLongIcon style={{ fontSize: '18px' }} />
            <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>{getTitle()}</Typography>
            {formData.BillNumber ? <Typography style={{ fontSize: '12px', opacity: 0.8 }}>{companyData.BillPrefix || '#'}{formData.BillNumber}</Typography> : null}
          </Box>
          <IconButton onClick={onClose} style={{ color: 'white', padding: '2px' }} size="small">
            <CloseIcon style={{ fontSize: '18px' }} />
          </IconButton>
        </DialogTitle>

        {/* ── Body ── */}
        <DialogContent style={{ padding: '10px 12px', flex: 1, overflow: 'auto' }}>

          {/* ══ HEADER SECTION ══ */}
          <Grid container spacing={1} alignItems="flex-start">

            {/* Row 1: Client | Bill Date | Bill Number | Fin Year | Remarks */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                disabled={isReadOnly}
                options={masters.filter(m => m.Type === 1)}
                value={selectedClient}
                onChange={handleClientSelect}
                getOptionLabel={(o) => o.Name || ''}
                isOptionEqualToValue={(o, v) => o.Id === v?.Id}
                renderOption={(props, o) => (
                  <li {...props} key={o.Id} style={{ fontSize: '12px', padding: '4px 10px' }}>
                    <span style={{ fontWeight: '600' }}>{o.Name}</span>
                    {o.ContactNumber && <span style={{ color: '#888', marginLeft: '6px', fontSize: '11px' }}>{o.ContactNumber}</span>}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Client *" size="small" placeholder="Select or type..."
                    error={!!errors.ClientName} helperText={errors.ClientName}
                    InputProps={{ ...params.InputProps, style: inp }}
                    InputLabelProps={{ style: lbl }}
                    FormHelperTextProps={{ style: hlp }}
                  />
                )}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '4px', fontSize: '12px', height: '30px', padding: '0 8px !important' }, '& .MuiAutocomplete-endAdornment': { top: '4px' } }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField name="BillDate" label="Bill Date" type="date" value={formData.BillDate}
                onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl, shrink: true }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={1.5}>
              <TextField name="BillNumber" label="Bill Number *" value={formData.BillNumber}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                InputProps={{ style: isReadOnly ? { ...inp, ...readBg } : inp }}
                InputLabelProps={{ style: lbl }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={1.5}>
              <TextField name="FinYear" label="Financial Year" value={formData.FinYear}
                onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                inputProps={{ maxLength: 5 }}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField name="Remarks" label="Remarks" value={formData.Remarks}
                onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                inputProps={{ maxLength: 250 }}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            {/* Row 2: Contact Number | Discount % */}
            <Grid item xs={12} sm={4} md={3}>
              <TextField name="ContactNumber" label="Contact Number" value={formData.ContactNumber}
                onChange={handleChange} fullWidth size="small" disabled={isReadOnly}
                inputProps={{ maxLength: 15 }}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <TextField name="DiscountRate" label="Discount %" value={formData.DiscountRate}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', min: 0, max: 100 }}
                InputProps={{ style: inp }} InputLabelProps={{ style: lbl }}
                FormHelperTextProps={{ style: hlp }}
              />
            </Grid>

            <Grid item xs={6} sm={3} md={2}>
              <FormControlLabel
                control={<Checkbox name="IsPettyCash" checked={formData.IsPettyCash} onChange={handleChange} disabled={isReadOnly} size="small"
                  sx={{ '&.Mui-checked': { color: '#5c3d1e' }, padding: '2px' }} />}
                label={<Typography style={{ fontSize: '11px' }}>Is Petty Cash</Typography>}
                style={{ margin: 0, flexShrink: 0 }}
              />
            </Grid>

          </Grid>

          {/* ══ DETAIL TABLE ══ */}
          <Box style={{ marginTop: '10px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #e0e0e0' }}>
            {/* Table header */}
            <Box style={{ background: 'linear-gradient(135deg, #2d3748 0%, #4a5568 100%)', display: 'flex', alignItems: 'center', padding: '0 8px', minHeight: '28px' }}>
              <Typography style={{ color: 'white', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', flex: 1 }}>
                Product Details
              </Typography>
              {!isReadOnly && (
                <Box style={{ display: 'flex', gap: '4px' }}>
                  <Button size="small" onClick={addRow}
                    style={{ background: '#38a169', color: 'white', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', textTransform: 'none', minHeight: '22px', border: 'none' }}>
                    <AddIcon style={{ fontSize: '14px', marginRight: '2px' }} /> Product
                  </Button>
                </Box>
              )}
            </Box>

            <Box style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                <thead>
                  <tr style={{ background: '#2d3748' }}>
                    {['WIDTH','HEIGHT','MNT-W','SIZE','FRAME','THICK','LENGTH','GLASS','MOUNT','BOARD','LAMN.','TAPE','HOOK','STAND','PRINT','QTY','RATE','DISC%','DISC AMT','AMOUNT',''].map(h => (
                      <th key={h} style={{ padding: '4px 6px', color: 'white', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px', whiteSpace: 'nowrap', textAlign: ['QTY','RATE','DISC%','DISC AMT','AMOUNT'].includes(h) ? 'right' : 'center' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {details.length === 0 ? (
                    <tr>
                      <td colSpan={isReadOnly ? 20 : 21} style={{ textAlign: 'center', padding: '16px', color: '#999', fontSize: '12px' }}>
                        No products added. Click "+ Product" to add.
                      </td>
                    </tr>
                  ) : details.map((row, idx) => (
                    <BillDetailRow
                      key={row._key || row.Id || idx}
                      row={row} idx={idx}
                      isReadOnly={isReadOnly}
                      isEditing={editingRow === idx}
                      onEdit={() => setEditingRow(idx)}
                      onChange={updateDetailField}
                      sizes={sizes} frames={frames} glasses={glasses} mounts={mounts}
                      boards={boards} laminations={laminations} tapes={tapes}
                      hooks={hooks} stands={stands} prints={prints}
                      lookupName={lookupName}
                    />
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>

          {/* ══ FOOTER TOTALS ══ */}
          <Box style={{ display: 'flex', gap: '6px', marginTop: '8px', paddingTop: '10px', flexWrap: 'nowrap', overflowX: 'auto' }}>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField label="Amount" value={parseFloat(formData.Amount || 0).toFixed(2)} fullWidth size="small" disabled
                InputProps={{ style: footerAmt }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="PackingCharge" label="Packing Charge" value={formData.PackingCharge}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="OtherAmount" label="Other Amount" value={formData.OtherAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField label="Roundoff" value={parseFloat(formData.Roundoff || 0).toFixed(2)} fullWidth size="small" disabled
                InputProps={{ style: footerAmt }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField label="Total Amount" value={parseFloat(formData.TotalAmount || 0).toFixed(2)} fullWidth size="small" disabled
                InputProps={{ style: { ...footerAmt, backgroundColor: '#e8f5e9' } }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="AdvanceAmount" label="Advance Amount" value={formData.AdvanceAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="PaidAmount" label="Cash Amount" value={formData.PaidAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="CardAmount" label="Card Amount" value={formData.CardAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField name="UpiAmount" label="UPI Amount" value={formData.UpiAmount}
                onChange={handleChange} fullWidth size="small" type="number" disabled={isReadOnly}
                inputProps={{ step: '0.01', style: { textAlign: 'right' } }}
                InputProps={{ style: footerInp }} InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
            <Box style={{ flex: 1, minWidth: '80px' }}>
              <TextField label="Balance Amount" value={parseFloat(formData.BalanceAmount || 0).toFixed(2)} fullWidth size="small" disabled
                InputProps={{ style: { ...footerAmt, backgroundColor: formData.BalanceAmount < 0 ? '#ffebee' : formData.BalanceAmount > 0 ? '#fff8e1' : '#e8f5e9', color: formData.BalanceAmount < 0 ? '#d32f2f' : 'inherit' } }}
                InputLabelProps={{ style: footerLbl, shrink: true }} />
            </Box>
          </Box>
          {errors.payment && (
            <Typography style={{ color: '#d32f2f', fontSize: '11px', marginTop: '4px' }}>
              ⚠ {errors.payment}
            </Typography>
          )}

        </DialogContent>

        {/* Actions */}
        <DialogActions style={{ padding: '8px 12px', background: '#f8f9fa', borderTop: '1px solid #e9ecef', gap: '6px', minHeight: 'auto', flexShrink: 0 }}>
          <Button onClick={onClose} style={{ color: '#6c757d', textTransform: 'none', fontWeight: '500', fontSize: '12px', padding: '4px 8px' }}>
            Cancel
          </Button>
          {(mode === 'view' || mode === 'edit') && (
            <Button onClick={handlePrintPDF} variant="outlined"
              style={{ borderColor: '#e65100', color: '#e65100', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 16px', fontSize: '12px' }}>
              🖨️ Print PDF
            </Button>
          )}
          {(mode === 'view' || mode === 'edit') && (
            <Button onClick={handleWhatsApp} variant="outlined"
              style={{ borderColor: '#25D366', color: '#25D366', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 16px', fontSize: '12px' }}>
              💬 WhatsApp
            </Button>
          )}
          {!isReadOnly && (
            <Button onClick={handleSave} disabled={loading} variant="contained"
              style={{ background: 'linear-gradient(135deg, #c9a84c 0%, #9a7a2e 100%)', color: 'white', textTransform: 'none', fontWeight: '600', borderRadius: '4px', padding: '4px 16px', fontSize: '12px' }}>
              {loading ? <CircularProgress size={14} style={{ color: 'white', marginRight: '6px' }} /> : null}
              {loading ? 'Saving...' : mode === 'create' ? 'Save Bill' : 'Update Bill'}
            </Button>
          )}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

// ─── Detail Row Sub-component ─────────────────────────────────────────────────
const BillDetailRow = ({ row, idx, isReadOnly, isEditing, onEdit, onChange,
  sizes, frames, glasses, mounts, boards, laminations, tapes, hooks, stands, prints, lookupName }) => {
  const cellStyle      = { padding: '3px 4px', borderBottom: '1px solid #f0f0f0', whiteSpace: 'nowrap' };
  const inputCellStyle = { padding: '2px 3px', borderBottom: '1px solid #f0f0f0' };
  const numInp = { fontSize: '11px', height: '26px', padding: '0 4px', borderRadius: '3px', border: '1px solid #ccc', width: '100%', textAlign: 'right', outline: 'none' };
  const selInp = { fontSize: '11px', height: '26px', padding: '0 2px', borderRadius: '3px', border: '1px solid #ccc', width: '100%', outline: 'none' };
  const rowBg  = idx % 2 === 0 ? '#ffffff' : '#fdf6ec';

  const sel = (field, arr, labelKey) => (
    <select value={row[field] || ''} disabled={isReadOnly}
      onChange={e => onChange(idx, field, e.target.value ? parseInt(e.target.value) : null)}
      style={selInp}>
      <option value="">—</option>
      {arr.map(x => <option key={x.Id} value={x.Id}>{x[labelKey || 'Name'] || x.Name}</option>)}
    </select>
  );

  const num = (field, w) => (
    <input type="number" value={row[field] ?? ''} disabled={isReadOnly}
      onChange={e => onChange(idx, field, e.target.value)}
      style={{ ...numInp, width: w || '52px' }} />
  );

  if (!isEditing && !isReadOnly) {
    return (
      <tr style={{ background: rowBg, cursor: 'pointer' }} onClick={onEdit}>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Width || '—'}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Height || '—'}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.MountWidth || '—'}</td>
        <td style={cellStyle}>{lookupName(sizes, row.SizeId)}</td>
        <td style={cellStyle}>{lookupName(frames, row.FrameId)}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Thicknes || '—'}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Length || '—'}</td>
        <td style={cellStyle}>{lookupName(glasses, row.GlassId)}</td>
        <td style={cellStyle}>{lookupName(mounts, row.MountId)}</td>
        <td style={cellStyle}>{lookupName(boards, row.BoardId)}</td>
        <td style={cellStyle}>{lookupName(laminations, row.LaminationId)}</td>
        <td style={cellStyle}>{lookupName(tapes, row.TapeId)}</td>
        <td style={cellStyle}>{row.HookCount > 0 ? row.HookCount : '—'}</td>
        <td style={cellStyle}>{lookupName(stands, row.StandId)}</td>
        <td style={cellStyle}>{prints.find(x => x.Id === row.PrintId)?.PrintType || '—'}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{row.Quantity}</td>
        <td style={{ ...cellStyle, textAlign: 'right', color: '#5c3d1e', fontWeight: '600' }}>{parseFloat(row.Rate || 0).toFixed(2)}</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{parseFloat(row.Discper || 0).toFixed(2)}%</td>
        <td style={{ ...cellStyle, textAlign: 'right' }}>{parseFloat(row.DiscAmt || 0).toFixed(2)}</td>
        <td style={{ ...cellStyle, textAlign: 'right', fontWeight: '700', color: '#2d3748' }}>₹{parseFloat(row.Amount || 0).toFixed(2)}</td>

      </tr>
    );
  }

  return (
    <tr style={{ background: '#fffbf0', outline: '2px solid #c9a84c' }}>
      <td style={inputCellStyle}>{num('Width', '50px')}</td>
      <td style={inputCellStyle}>{num('Height', '50px')}</td>
      <td style={inputCellStyle}>{num('MountWidth', '50px')}</td>
      <td style={inputCellStyle}>{sel('SizeId', sizes)}</td>
      <td style={inputCellStyle}>{sel('FrameId', frames)}</td>
      <td style={inputCellStyle}>{num('Thicknes', '50px')}</td>
      <td style={inputCellStyle}>{num('Length', '50px')}</td>
      <td style={inputCellStyle}>{sel('GlassId', glasses)}</td>
      <td style={inputCellStyle}>{sel('MountId', mounts)}</td>
      <td style={inputCellStyle}>{sel('BoardId', boards)}</td>
      <td style={inputCellStyle}>{sel('LaminationId', laminations)}</td>
      <td style={inputCellStyle}>{sel('TapeId', tapes)}</td>
      <td style={inputCellStyle}>
        <input type="number" value={row.HookCount ?? 0} disabled={isReadOnly}
          onChange={e => onChange(idx, 'HookCount', e.target.value)}
          style={{ ...numInp, width: '52px' }} placeholder="0" />
      </td>
      <td style={inputCellStyle}>{sel('StandId', stands)}</td>
      <td style={inputCellStyle}>{sel('PrintId', prints, 'PrintType')}</td>
      <td style={inputCellStyle}>{num('Quantity', '44px')}</td>
      <td style={{ ...inputCellStyle, textAlign: 'right', color: '#5c3d1e', fontWeight: '700', minWidth: '65px', fontSize: '11px' }}>
        {parseFloat(row.Rate || 0).toFixed(2)}
      </td>
      <td style={inputCellStyle}>{num('Discper', '48px')}</td>
      <td style={inputCellStyle}>{num('DiscAmt', '52px')}</td>
      <td style={{ ...inputCellStyle, textAlign: 'right', fontWeight: '700', color: '#2d3748', minWidth: '70px' }}>
        ₹{parseFloat(row.Amount || 0).toFixed(2)}
      </td>

    </tr>
  );
};

export default BillModal;
